import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import { useDataLayerContext, useDataLayerPopulator } from "../ContextLayer/DataProvider/DataProvider";
import { QuickEntryModalAtom } from "./QuickEntryModal";
import { useAddWordMutation } from "../../hooks/engineHooks";
import { DictionaryResponseType } from "../ContextLayer/DataProvider/types";
import { useSettingsStorageContext } from "../ContextLayer/SettingsStorageProvider";
import CheckBox from "../CheckBox";

const QuickEntryInputs = styled.div`
  width: 100%;
  padding-left: 15px;
`

const QuickEntryRowAtom = styled.div<{$darkMode: boolean}>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 5px;

  width: 100%;
  height: 37px;

  font-family: 'FixelVariable', 'Fira Code', sans-serif;
  font-size: 1.1rem;
  font-weight: 500;
  color: ${props => props.$darkMode ? "#FEF7E2" : "#837772ff"};
  transition: color var(--transition-duration-normal);

  // padding: 10px 8px 10px 20px;

  cursor: text;

  box-sizing: border-box;
`

const QuickEntryTitleAtom = styled.div<{$darkMode: boolean}>`
  font-family: 'Fixel Variable';
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: ${props => props.$darkMode ? "#FEF7E2" : "#837772ff"};
  white-space: nowrap;
  text-shadow: 2px 2px 4px rgba(255, 112, 112, 0.09);

  ${QuickEntryModalAtom} & {
    color: ${props => props.$darkMode ? "#928279ff" : "#B58B77"};
  }

  transition: color var(--transition-duration-normal);
`

const QuickEntryInputAtom = styled.input<{$darkMode: boolean}>`
  background-color: transparent;
  border: 0px;
  font-family: 'FixelVariable', 'Fira Code', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => props.$darkMode ? "#FEF7E2" : "#50443fff"};

  ${QuickEntryModalAtom} & {
    color: ${props => props.$darkMode ? "#928279ff" : "#B58B77"};
  }

  width: 100%;

  &:focus {
    outline: none;
    box-shadow: none;
  }

  &::placeholder {
    font-family: 'Fixel Italic Variable', 'FixelVariable', 'Fira Code', sans-serif;
    font-style: italic;
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;

    color: ${props => props.$darkMode ? "#fef7e28f" : "#a778626d"};
    ${QuickEntryModalAtom} & {
      color: ${props => props.$darkMode ? "#e4d8d1ff" : "#B58B77"};
    }

    transition: color var(--transition-duration-normal);
  }
`

const GreenButtonAtom = styled.button`
  height: 100%;
  border: 0px;
  border-radius: 3px;
  font-family: 'FixelVariable', 'Fira Code', sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  color: white;
  background: #c8cdbe;
  padding: 3px 20px 3px 20px;
  cursor: pointer;

  transition: background var(--transition-duration-normal);

  ${QuickEntryModalAtom} & {
    background: #8eab59ff;
  }

  &:focus {
    background-color: #9cc256ff;
    outline: none;
    box-shadow: none;
  }
`

const QuickEntryAtom = styled.div<{ $unwrapped: boolean, $modal: boolean, $darkMode: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  // gap: 10px;

  width: 100%;
  height: ${props => props.$unwrapped ? 140 : 40}px !important;

  border: 1px solid ${props => props.$darkMode ? "rgb(255 247 234 / 0%)" : "rgb(255 247 234 / 48%)"};
  border-radius: 20px;

  box-shadow: 6px 6px 18px rgba(0, 0, 0, 0.07);
  background-color: ${props => props.$modal ? "#FFFAF5" : props.$darkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(255, 255, 255, 0.05)"};
  backdrop-filter: blur(25px);

  padding: 0px;
  margin: 0px;

  box-sizing: border-box;

  overflow: hidden;

  transition: background-color var(--transition-duration-normal), color var(--transition-duration-normal), height var(--transition-duration-normal), border var(--transition-duration-normal);

  &:hover {
    background-color: ${props => props.$modal ? "#FFFAF5" : "rgba(254, 215, 180, 0.18)"};
  }

  &:hover ${QuickEntryTitleAtom} {
    color: #B58B77;
  }

  &:hover ${GreenButtonAtom} {
    background: #8eab59ff;
  }
`

type QuickEntryProps = {
  modal?: boolean
}

export default function QuickEntry({modal = false}: QuickEntryProps) {
  const [unwrapped, setUnwrapped] = useState<boolean>(modal);
  const { settings } = useSettingsStorageContext();

  const wordRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);
  const tagsRef = useRef<HTMLInputElement>(null);
  const populateDataLayer = useDataLayerPopulator();
  const { dataLayer, setDataLayer } = useDataLayerContext();

  const addWordMutation = useAddWordMutation();

  const placeholderRevelaer = (e: React.FocusEvent) => setUnwrapped(true);

  const [ keepTheTag, setKeepTheTag ] = useState<boolean>(false);

  useEffect(() => {
    if(modal) {
      (wordRef.current as HTMLInputElement).focus();
      return;
    }

    const globalClickHandler = (e: MouseEvent) => {
      let targetElement = (e.target as HTMLElement);

      let found = false;
      while (!found && targetElement !== null && targetElement.parentElement !== null) {
        if (targetElement.getAttribute('id') === 'quickEntry') {
          found = true;
          break;
        }
        if (targetElement === null || targetElement.parentElement === null) break;
        targetElement = targetElement.parentElement;
      }

      if (!found) {
        if (unwrapped) {
          (wordRef.current as HTMLInputElement).value = '';
          (descriptionRef.current as HTMLInputElement).value = '';
          if(!keepTheTag) (tagsRef.current as HTMLInputElement).value = '';
          setUnwrapped(false);
        }
      }
    };

    document.addEventListener("click", globalClickHandler);

    return () => {
      document.removeEventListener("click", globalClickHandler);
    }
  }, [modal, unwrapped, keepTheTag]);

  const submit = () => {
    const word = (wordRef.current as HTMLInputElement).value;
    const description = (descriptionRef.current as HTMLInputElement).value;
    if (!word || !description) return;

    let tags = ((tagsRef.current as HTMLInputElement).value).split(',').map(t => t.trim());
    if (tags.length === 1 && tags[0] === '') tags = [];

    // console.log(`${word}, ${description}, ${JSON.stringify(tags)}`);
    addWordMutation.mutateAsync({
      word,
      description,
      tags
    });
  }

  useEffect(() => {
    if (addWordMutation.isSuccess) {
      const data: DictionaryResponseType = addWordMutation.data;
      if (data?.ready) {
        const aggregationData = populateDataLayer(data.dictionary);
        setDataLayer({
          ...aggregationData,
          tags: dataLayer.tags
        });
        (wordRef.current as HTMLInputElement).value = '';
        (descriptionRef.current as HTMLInputElement).value = '';
        if(!keepTheTag) (tagsRef.current as HTMLInputElement).value = '';
        (wordRef.current as HTMLInputElement).focus();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addWordMutation.isSuccess, addWordMutation.data, dataLayer.tags, keepTheTag])

  return (
    <QuickEntryAtom id="quickEntry" className={unwrapped ? "open" : ""} $unwrapped={unwrapped} $modal={modal} $darkMode={settings.darkMode}>
      <QuickEntryInputs>
        <QuickEntryRowAtom $darkMode={settings.darkMode}>
          <QuickEntryTitleAtom onClick={() => (wordRef.current as HTMLInputElement).focus()} $darkMode={settings.darkMode}>Add a word</QuickEntryTitleAtom>
          <QuickEntryInputAtom id="wordInput" ref={wordRef} placeholder='La tavola' onFocus={placeholderRevelaer} onBlur={placeholderRevelaer} $darkMode={settings.darkMode} />
        </QuickEntryRowAtom>
        <QuickEntryRowAtom $darkMode={settings.darkMode}>
          <QuickEntryTitleAtom onClick={() => (descriptionRef.current as HTMLInputElement).focus()} $darkMode={settings.darkMode}>Description</QuickEntryTitleAtom>
          <QuickEntryInputAtom id="descriptionInput" ref={descriptionRef} placeholder='The table' $darkMode={settings.darkMode} />
        </QuickEntryRowAtom>
        <QuickEntryRowAtom $darkMode={settings.darkMode} style={{paddingRight: '5px'}}>
          <QuickEntryTitleAtom onClick={() => (tagsRef.current as HTMLInputElement).focus()} $darkMode={settings.darkMode}>Tags</QuickEntryTitleAtom>
          <QuickEntryInputAtom id="tagsInput" ref={tagsRef} placeholder='i mobili, the furniture' $darkMode={settings.darkMode} />
          <CheckBox selected={keepTheTag} onClick={() => setKeepTheTag(!keepTheTag)}/>
        </QuickEntryRowAtom>
      </QuickEntryInputs>
      <GreenButtonAtom onClick={submit}>add</GreenButtonAtom>
    </QuickEntryAtom>
  );
}